import { skipToken } from '@reduxjs/toolkit/query';
import { FilterParams } from '../domain/filter';
import { CampaignListEntry } from '../slice/schemas';
import {
  useGetCpgPartnerTrendsQuery,
  useGetCpgWeeklyTrendsQuery,
  useGetLci3WeeklyTrendChartsQuery,
  useGetRslWeeklyTrendsQuery,
} from '../slice/api';

const isApprovedForTrending = (status: CampaignListEntry['approvalStatus']) =>
  status === 'approved' || status === 'approved_visit_metrics';

export const useTrends = (
  filterParams: FilterParams,
  campaignListData: CampaignListEntry[]
) => {
  const shouldFetchTrends =
    filterParams.selectedReportType === 'overall' &&
    campaignListData.length > 0;

  const isVisitationWeekly = (c: CampaignListEntry) =>
    c.campaignType === 'visitation' &&
    c.lciVersion >= 3 &&
    c.reportType === 'weekly' &&
    isApprovedForTrending(c.approvalStatus);

  const isCpgWeekly = (c: CampaignListEntry) =>
    c.campaignType === 'cpg' &&
    c.reportType === 'weekly' &&
    isApprovedForTrending(c.approvalStatus);

  const isCpgPartner = (c: CampaignListEntry) =>
    c.campaignType === 'cpg' &&
    c.reportType === 'partner' &&
    isApprovedForTrending(c.approvalStatus);

  const isRslWeekly = (c: CampaignListEntry) =>
    c.campaignType === 'rsl' &&
    c.reportType === 'weekly' &&
    isApprovedForTrending(c.approvalStatus);

  const visitationWeeklyCampaignIds = shouldFetchTrends
    ? campaignListData.filter(c => isVisitationWeekly(c)).map(r => r.campaignId)
    : [];

  const { data: trendsData, isFetching: isTrendsLoading } =
    useGetLci3WeeklyTrendChartsQuery(
      visitationWeeklyCampaignIds.length > 0
        ? { campaign_ids: visitationWeeklyCampaignIds }
        : skipToken
    );

  const cpgWeeklyCampaignIds = shouldFetchTrends
    ? campaignListData.filter(c => isCpgWeekly(c)).map(c => c.campaignId)
    : [];

  const cpgPartnerCampaigns = shouldFetchTrends
    ? campaignListData.filter(c => isCpgPartner(c))
    : [];

  const { data: cpgWeeklyTrendsData, isFetching: isCpgWeeklyTrendsLoading } =
    useGetCpgWeeklyTrendsQuery(
      cpgWeeklyCampaignIds.length > 0
        ? {
            campaign_ids: cpgWeeklyCampaignIds,
          }
        : skipToken
    );

  const { data: cpgPartnerTrendsData, isFetching: isCpgPartnerTrendsLoading } =
    useGetCpgPartnerTrendsQuery(
      cpgPartnerCampaigns.length > 0
        ? {
            trends: cpgPartnerCampaigns.map(campaign => ({
              campaign_id: campaign.campaignId,
              channel: campaign.partnerSlug ?? '',
            })),
          }
        : skipToken
    );

  const rslWeeklyCampaignIds = shouldFetchTrends
    ? campaignListData.filter(c => isRslWeekly(c)).map(c => c.campaignId)
    : [];

  const { data: rslWeeklyTrendsData, isFetching: isRslWeeklyTrendsLoading } =
    useGetRslWeeklyTrendsQuery(
      rslWeeklyCampaignIds.length > 0
        ? {
            campaign_ids: rslWeeklyCampaignIds,
          }
        : skipToken
    );

  return {
    trendsData,
    isTrendsLoading,
    cpgWeeklyTrendsData,
    isCpgWeeklyTrendsLoading,
    cpgPartnerTrendsData,
    isCpgPartnerTrendsLoading,
    rslWeeklyTrendsData,
    isRslWeeklyTrendsLoading,
  };
};

export type TrendsHook = ReturnType<typeof useTrends>;
